/** Typed API client for the AgriLens backend.
 *
 * Every call is a thin `fetch` wrapper that:
 *   - Prefixes VITE_API_BASE_URL (empty by default → same origin +
 *     ingress rewrites /api → backend).
 *   - Attaches the JWT from `auth.loadToken()` when present.
 *   - Throws a typed `ApiError` on non-2xx so react-query surfaces
 *     the actual backend error in the UI instead of "Network error".
 */

import { clearToken, loadToken } from "./auth";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal } = opts;

  let url = `${API_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = loadToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  const ct = resp.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  const payload = isJson ? await resp.json().catch(() => null) : await resp.text();

  if (!resp.ok) {
    if (resp.status === 401) {
      // Token no longer valid — force re-login on the next render.
      clearToken();
    }
    const msg = (isJson && payload && (payload as { error?: string }).error)
      ? (payload as { error: string }).error
      : `${resp.status} ${resp.statusText}`;
    throw new ApiError(resp.status, payload, msg);
  }
  return payload as T;
}

// ---------------------------------------------------------------------
// Domain endpoints
// ---------------------------------------------------------------------

// --- auth -----------------------------------------------------------
export interface LoginResp {
  token: string;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    enterprise_id: number | null;
  };
}
export const login = (email: string, password: string) =>
  request<LoginResp>("/api/auth/login", {
    method: "POST",
    body: { email, password },
  });

/** Regular-mode self-signup: email + password creates a standalone
 * personal account (the backend auto-provisions a personal enterprise
 * + default godown). Returns a JWT so the caller lands signed in. */
export const signup = (args: {
  email: string;
  password: string;
  first_name: string;
  last_name?: string;
}) =>
  request<LoginResp>("/api/auth/signup", { method: "POST", body: args });

// --- passwordless phone login (OTP) --------------------------------
export interface OtpRequestResp {
  sent: boolean;
  expires_in: number;
  /** Present only while WhatsApp delivery isn't configured (dev): the
   * code is echoed back so the flow is testable end-to-end. Never
   * appears once real delivery is live. */
  dev_code?: string;
  error?: string;
}

/** Request a one-time code for `phone`. The backend only sends codes to
 * numbers that already belong to a user or match a live phone invite;
 * anything else comes back 404 so we don't leak who's registered. */
export const requestOtp = (phone: string) =>
  request<OtpRequestResp>("/api/auth/otp/request", {
    method: "POST",
    body: { phone },
  });

/** Verify a one-time code and receive a JWT. For a brand-new (invited)
 * account the optional name fields seed the profile; they're ignored
 * when the number already belongs to a user. */
export const verifyOtp = (args: {
  phone: string;
  code: string;
  first_name?: string;
  last_name?: string;
}) =>
  request<LoginResp>("/api/auth/otp/verify", { method: "POST", body: args });

/** The signed-in user's profile (name, role, enterprise) — the JWT
 * only carries email/role/scopes, so anything needing the real name
 * (e.g. the greeting) fetches this. */
export interface Me {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  enterprise_id: number | null;
  mobile?: string | null;
  /** Signed, short-lived URL to the avatar blob, or null. */
  avatar_url?: string | null;
  last_login_at?: string | null;
  created_at?: string | null;
}
export const me = () => request<{ user: Me }>("/api/auth/me");

/** Self-service profile edit: a user may change only their own name,
 * mobile, and avatar. `avatar_base64` accepts a data: URL (or raw
 * base64); pass an empty string to clear the picture. */
export const updateMe = (args: {
  first_name?: string;
  last_name?: string;
  mobile?: string;
  avatar_base64?: string;
}) =>
  request<{ user: Me }>("/api/auth/me", { method: "PATCH", body: args });

// --- users ---------------------------------------------------------
/** A teammate in the caller's tenant, as returned by GET /api/users.
 * State/district heads may list this to put a real name against an
 * email — the JWT and invitations only carry the email. `scopes`
 * tells us which part of the tree the user was granted, so we can
 * match, say, the district head to their district. */
export interface PortalUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  enterprise_id: number | null;
  scopes: { scope_type: string; scope_id: number }[];
}
export const listUsers = () =>
  request<{ users: PortalUser[] }>("/api/users");

// --- invitations ---------------------------------------------------
export interface Invitation {
  id: number;
  /** Exactly one of email / phone identifies the invitee. Email invites
   * accept via the token link; phone invites accept passwordless by
   * signing in with the number (OTP). */
  email: string | null;
  phone: string | null;
  role: string;
  target_scope_type: string;
  target_scope_id: number;
  enterprise_id: number | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
export interface InvitePreview {
  email: string;
  role: string;
  enterprise_id: number;
  scopes: { scope_type: string; scope_id: number }[];
  expires_at: string;
}
export const previewInvite = (token: string) =>
  request<InvitePreview>(`/api/invitations/preview/${encodeURIComponent(token)}`);

export const acceptInvite = (
  token: string, first_name: string, last_name: string, password: string,
) =>
  // Backend route is POST /api/invitations/<token>/accept — the token
  // is a path segment, the profile fields go in the body.
  request<LoginResp>(
    `/api/invitations/${encodeURIComponent(token)}/accept`,
    {
      method: "POST",
      body: { first_name, last_name, password },
    },
  );

/** Generic invite creation — used to invite the next role down the
 * cascade (enterprise_admin→state_head, state_head→district_head,
 * district_head→godown_qc_agent). The backend enforces the cascade
 * rules; we just pass the target scope. Returns the invitation plus,
 * in dev mode (no email configured), a raw `accept_token` for
 * building the accept link by hand. */
export interface InviteCreateResp {
  invitation: Invitation;
  email_sent: boolean;
  accept_token?: string;
}
export const createInvitation = (args: {
  /** Provide exactly one of email or phone. Phone invites onboard
   * passwordless: the invitee signs in with their number (OTP) and the
   * account is created on first verify. */
  email?: string;
  phone?: string;
  role: string;
  target_scope_type: "enterprise" | "state" | "district" | "godown";
  target_scope_id: number;
}) =>
  request<InviteCreateResp>("/api/invitations", {
    method: "POST",
    body: args,
  });

export const listInvitations = () =>
  request<{ invitations: Invitation[] }>("/api/invitations");

export const revokeInvitation = (id: number) =>
  request<{ invitation: Invitation }>(`/api/invitations/${id}/revoke`, {
    method: "POST",
  });

// --- rollups -------------------------------------------------------
export interface RollupBucket {
  bucket_start: string;
  bucket_end: string;
  rst_count: number;
  net_weight_kg_total: number;
  sample_count: number;
  pass_count: number;
  fail_count: number;
  incomplete_count: number;
  grain_type_breakdown: Record<
    string,
    { sample_count: number; pass: number; fail: number; incomplete: number }
  >;
}
export interface RollupResponse {
  scope_type: string;
  scope_id: number;
  granularity: "day" | "week" | "month";
  date_from: string;
  date_to: string;
  computed_in_ms: number;
  buckets: RollupBucket[];
}

export const rollupFor = (
  scopeType: "godown" | "district" | "state" | "enterprise",
  scopeId: number,
  params: { from?: string; to?: string; granularity?: "day" | "week" | "month" } = {},
) =>
  request<RollupResponse>(`/api/rollups/${scopeType}/${scopeId}`, {
    query: {
      from: params.from,
      to: params.to,
      granularity: params.granularity,
    },
  });

export interface DayRollupResponse {
  date: string;
  computed_in_ms: number;
  scopes: {
    scope_type: string;
    scope_id: number;
    scope_name: string;
    bucket: RollupBucket;
  }[];
}
export const rollupForDay = (day: string) =>
  request<DayRollupResponse>(`/api/rollups/day/${day}`);

// --- enterprises ---------------------------------------------------
export interface Enterprise {
  id: number;
  name: string;
  slug: string;
  timezone: string;
  logo_url?: string | null;
}
export const listEnterprises = () =>
  request<{ enterprises: Enterprise[] }>("/api/enterprises");

export const getEnterprise = (id: number) =>
  request<{ enterprise: Enterprise }>(`/api/enterprises/${id}`);

/** The caller's own enterprise (name + logo) for portal branding.
 * Works for every tenant role; returns null for agrilens_super. */
export const myEnterprise = () =>
  request<{ enterprise: Enterprise | null }>("/api/enterprises/mine");

/** Edit an enterprise. Pass logo_base64 (raw or data: URL) to set the
 * logo, or null/"" to clear it. Only super + enterprise_admin. */
export const updateEnterprise = (
  id: number,
  args: { name?: string; timezone?: string; logo_base64?: string | null },
) =>
  request<{ enterprise: Enterprise; logo_warning?: string }>(
    `/api/enterprises/${id}`,
    { method: "PATCH", body: args },
  );

export const createEnterprise = (args: {
  name: string;
  slug?: string;
  timezone?: string;
}) =>
  request<{ enterprise: Enterprise }>("/api/enterprises", {
    method: "POST",
    body: args,
  });

/** Bootstrap an enterprise with its first admin. Super-only,
 * hard-codes role=enterprise_admin + scope=enterprise, so the caller
 * only supplies the email. */
export const inviteEnterpriseAdmin = (enterpriseId: number, email: string) =>
  request<InviteCreateResp>(`/api/enterprises/${enterpriseId}/admins`, {
    method: "POST",
    body: { email },
  });

// --- states --------------------------------------------------------
export interface State {
  id: number;
  enterprise_id: number;
  name: string;
  code: string;
}
export const listStates = (enterpriseId?: number) =>
  request<{ states: State[] }>("/api/states", {
    query: enterpriseId ? { enterprise_id: enterpriseId } : undefined,
  });

export const getState = (id: number) =>
  request<{ state: State }>(`/api/states/${id}`);

export const createState = (args: {
  enterprise_id: number;
  name: string;
  code: string;
}) =>
  request<{ state: State }>("/api/states", { method: "POST", body: args });

// --- districts -----------------------------------------------------
export interface District {
  id: number;
  state_id: number;
  enterprise_id: number;
  name: string;
  code?: string | null;
  zone?: string | null;
}
export const listDistricts = (args: {
  state_id?: number;
  enterprise_id?: number;
}) =>
  request<{ districts: District[] }>("/api/districts", {
    query: {
      state_id: args.state_id,
      enterprise_id: args.enterprise_id,
    },
  });

export const getDistrict = (id: number) =>
  request<{ district: District }>(`/api/districts/${id}`);

/** Delete a district. Backend refuses (409) while it still holds
 * godowns, and scope-checks write access. */
export const deleteDistrict = (id: number) =>
  request<{ ok: true }>(`/api/districts/${id}`, { method: "DELETE" });

export const createDistrict = (args: {
  state_id: number;
  name: string;
  code?: string;
  zone?: string;
}) =>
  request<{ district: District }>("/api/districts", {
    method: "POST",
    body: args,
  });

// --- godowns -------------------------------------------------------
/** The seven FCI FAQ limit knobs, shared by every grain config and by
 * the legacy single-grain columns. All are percentages except a few
 * counts, but the UI treats them uniformly as numeric thresholds. */
export interface GrainLimits {
  moisture: number;
  dd: number;
  b: number;
  i: number;
  ddbi: number;
  fm: number;
  idk: number;
}

/** One grain type configured on a godown, with its limit set. A
 * godown always has ≥ 1 (the backend refuses to remove the last). */
export interface GrainConfig {
  grain_type: string;
  limits: GrainLimits;
}

/** A godown's operational lifecycle status. Heads set it from the edit
 * drawer; the portal renders a friendly label for each token. */
export type GodownStatus = "setup" | "filling" | "filled";

export interface Godown {
  id: number;
  name: string;
  place?: string | null;
  enterprise_id: number;
  district_id: number | null;
  /** Operational status: "setup" | "filling" | "filled". */
  status?: GodownStatus;
  /** Primary grain (first config) — mirrors backend to_dict. */
  grain_type?: string;
  /** Limits of the primary grain, for a quick summary. */
  limits?: GrainLimits;
  /** Full per-grain config list. */
  grain_configs?: GrainConfig[];
  user_count?: number;
  client_name?: string | null;
  client_address?: string | null;
  client_other_details?: string | null;
  created_at?: string;
}
export const listGodowns = () =>
  request<{ godowns: Godown[] }>("/api/godowns");

export const createGodown = (args: {
  district_id: number;
  name: string;
  place?: string;
}) =>
  request<{ godown: Godown }>("/api/godowns", { method: "POST", body: args });

/** Edit a godown's name, place, and/or operational status. Backend
 * scope-checks write access (a district head over their own district's
 * godowns). */
export const updateGodown = (
  godownId: number,
  args: { name?: string; place?: string; status?: GodownStatus },
) =>
  request<{ godown: Godown }>(`/api/godowns/${godownId}`, {
    method: "PATCH",
    body: args,
  });

/** Delete a godown. Backend refuses (409) while QC agents are still
 * assigned to it. */
export const deleteGodown = (godownId: number) =>
  request<{ ok: true }>(`/api/godowns/${godownId}`, { method: "DELETE" });

/** Unassign a QC agent from a godown: drops their godown scope so
 * they lose access on their next request. */
export const removeGodownAgent = (godownId: number, userId: number) =>
  request<{ ok: true }>(`/api/godowns/${godownId}/agents/${userId}`, {
    method: "DELETE",
  });

/** Upsert the limits for ONE grain type on ONE godown. Creates the
 * grain config if absent, else updates just its limits — every other
 * grain on the godown is left untouched. Returns the whole godown. */
export const upsertGrainConfig = (
  godownId: number,
  grainType: string,
  limits: GrainLimits,
) =>
  request<{ godown: Godown }>(
    `/api/godowns/${godownId}/grain-configs/${encodeURIComponent(grainType)}`,
    { method: "PUT", body: { limits } },
  );

/** Remove one grain config from a godown. Backend 409s if it's the
 * last one (a godown must keep ≥ 1 grain to accept samples). */
export const deleteGrainConfig = (godownId: number, grainType: string) =>
  request<{ godown: Godown }>(
    `/api/godowns/${godownId}/grain-configs/${encodeURIComponent(grainType)}`,
    { method: "DELETE" },
  );

// --- reports -------------------------------------------------------
/** A saved grading report. District heads (and every higher scope)
 * list these to audit the QC agents working under their godowns —
 * the backend scope-filters `GET /api/reports` so each role sees
 * exactly the reports in their part of the tree. */
export interface Report {
  id: number;
  report_id: string;
  user_id: number;
  user_name: string | null;
  enterprise_id: number | null;
  rst_id: number | null;
  godown_id: number | null;
  godown_name: string | null;
  grain_type: string | null;
  verdict: string;
  kernels: number;
  moisture: number | null;
  gps_lat: number | null;
  gps_lon: number | null;
  model: string | null;
  notes: string | null;
  created_at: string;
}
export const listReports = () =>
  request<{ reports: Report[] }>("/api/reports");

/** Public URL for a report's stored PDF. Served by the backend under
 * /api/verify (the same /api/* path the API host routes to the
 * backend — the bare /verify host path isn't exposed cross-origin).
 * 404s when no PDF was uploaded for that report, so treat as
 * best-effort. */
export const verifyUrl = (reportId: string) =>
  `${API_BASE}/api/verify?report_id=${encodeURIComponent(reportId)}`;

/** Same stored PDF, but served INLINE (Content-Disposition: inline) so
 * a browser renders it in its built-in PDF viewer instead of
 * downloading it. Used by the portal's embedded report viewer (iframe /
 * open-in-new-tab); `verifyUrl` above stays the plain download link. */
export const verifyViewUrl = (reportId: string) =>
  `${API_BASE}/api/verify?report_id=${encodeURIComponent(reportId)}` +
  `&disposition=inline`;

// --- password reset ------------------------------------------------
export interface ResetRequestResp {
  email: string;
  email_sent: boolean;
  reset_token: string;
  expires_at: string;
}
/** Admin-triggered reset. Returns the raw token so the portal can
 * show a copy-link fallback even when email is down. */
export const requestPasswordReset = (email: string) =>
  request<ResetRequestResp>("/api/password/reset-requests", {
    method: "POST",
    body: { email },
  });

/** Consume a reset token and set a new password. Returns a JWT so
 * the user lands signed in. */
export const resetPassword = (token: string, password: string) =>
  request<LoginResp>(`/api/password/reset/${encodeURIComponent(token)}`, {
    method: "POST",
    body: { password },
  });

/** Self-serve "forgot password". Always resolves with a generic
 * message (no account enumeration); the link is delivered by email
 * only. */
export const forgotPassword = (email: string) =>
  request<{ message: string }>("/api/password/forgot", {
    method: "POST",
    body: { email },
  });
